<script>
   /****************************************************************
   * ExtremePlot                                                   *
   * --------------------                                          *
   * shows plot with number of extreme samples for different alpha *
   *****************************************************************/

   import { pchisq } from 'mdatools/distributions';
   import { Vector } from 'mdatools/arrays';
   import { Axes, XAxis, YAxis, Box, Lines, Segments } from 'svelte-plots-basic/2d';
   import { pcares_get, pcamodel_get_distparams} from '../wasm/pcvpca';

   import PlotSeries from './PlotSeries.svelte';
   import AppLegend from './AppLegend.svelte';


   /*****************************************/
   /* Input parameters                      */
   /*****************************************/

   export let model;                            // PCA model object
   export let res;                              // JSON with results
   export let params;                           // JSON with main plot parameters

   // constant parameters
   export let margins = [0.65, 0.75, 0.1, 0.5];    // axes margins
   export let showGrid = true;                  // logical, show grid or not
   export let xLabel = 'Number of extremes (expected)';
   export let yLabel = 'Number of extremes (observed)';

   // function to compute observed extremes
   function get_observed(res, hParams, qParams, params) {

      if (!params || !params.ncomp) return undefined;
      if (!res || !hParams || !qParams || params.ncomp < 0) return undefined;

      const h0 = hParams.u0[params.ncomp - 1];
      const Nh = hParams.Nu[params.ncomp - 1];
      const q0 = qParams.u0[params.ncomp - 1];
      const Nq = qParams.Nu[params.ncomp - 1];

      const h = pcares_get(res, "hdist", params.ncomp);
      const q = pcares_get(res, "qdist", params.ncomp);

      // loop over rows to make predictions
      let observed = Vector.zeros(nObj);
      for (let i = 0; i < nObj; i++) {
         const fi  = h[i] / h0 * Nh + q[i] / q0 * Nq;
         const pi = pchisq(fi, Nq + Nh);
         observed = observed.add((alpha.apply(v => 1 - pi < v)));
      }
      return observed;
   }

   $: title = params ? 'Extreme plot (A = ' + params.ncomp + ')' : '';

   // distance distribution parameters (only for moments)
   let hParams, qParams;
   $: if (model) {
      hParams = pcamodel_get_distparams(model, "h", "classic");
      qParams = pcamodel_get_distparams(model, "q", "classic");
   } else {
      hParams = undefined;
      qParams = undefined;
   }

   // number of observations
   $: nObj = res ? res['cal'].nrows : undefined;

   // compute expected values values
   let expected, Nm, Np, alpha;
   $: if (nObj) {
      alpha = Vector.zeros(nObj);
      expected = Vector.zeros(nObj);
      Nm = Vector.zeros(nObj);
      Np = Vector.zeros(nObj);

      for (let i = 1; i <= nObj; i++) {
         const a = i / nObj;
         const D = 2 * Math.sqrt(i * (1 - a));
         Nm.v[i - 1] = i - D;
         Np.v[i - 1] = i + D;
         alpha.v[i - 1] = a;
         expected.v[i - 1] = i;
      }

   } else {
      expected = undefined;
      Nm = undefined;
      Np = undefined;
      alpha = undefined;
   }

   $: ps = params && res ? Object.keys(res).map(id => ({
      'id': id,
      'x': expected,
      'y': get_observed(res[id], hParams, qParams, params)
   })) : null;
</script>

{#if params }
<Axes {margins} {title} {xLabel} {yLabel}
   limX={[-1, nObj + 2]}
   limY={[-1, nObj + 2]}>

   <Lines xValues={expected} yValues={expected} lineColor="#e0e0e0" />
   <Lines xValues={expected} yValues={Nm} lineColor="#e0e0e0" />
   <Lines xValues={expected} yValues={Np} lineColor="#e0e0e0" />
   <Segments xStart={expected} xEnd={expected} yStart={Nm} yEnd={Np}  lineColor="#e0e0e0"/>

   {#each ps as series}
   <PlotSeries values={series} type="p" />
   {/each}

   <AppLegend position="bottomright"/>
   <XAxis {showGrid} slot="xaxis" />
   <YAxis {showGrid} slot="yaxis" />
   <Box slot="box" />
</Axes>
{/if}